/**
 * Notification Toast — top-right corner toasts
 */
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import { useNotifications } from './NotificationContext';
import type { NotificationType } from './types';

const CONFIG: Record<NotificationType, { icon: React.ElementType; color: string; bg: string }> = {
  success: { icon: CheckCircle,   color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  warning: { icon: AlertTriangle, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  error:   { icon: AlertCircle,   color: '#ef4444', bg: 'rgba(239,68,68,0.1)'  },
  info:    { icon: Info,          color: '#6366f1', bg: 'rgba(99,102,241,0.1)' },
}

export default function NotificationToast() {
  const { notifications, dismiss } = useNotifications()

  if (notifications.length === 0) { return null }

  return (
    <div style={{
      position: 'fixed',
      top: '44px',
      right: '16px',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      pointerEvents: 'none',
    }}>
      {notifications.map(n => {
        const { icon: Icon, color, bg } = CONFIG[n.type]
        return (
          <div
            key={n.id}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              padding: '10px 14px',
              background: bg,
              border: `1px solid ${color}30`,
              borderRadius: '10px',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              minWidth: '260px',
              maxWidth: '320px',
              pointerEvents: 'all',
              animation: 'slideIn .25s ease-out',
            }}
          >
            <Icon size={16} color={color} style={{ flexShrink: 0, marginTop: '1px' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color }}>{n.title}</div>
              {n.message && (
                <div style={{ fontSize: '11px', color: 'var(--os-muted)', marginTop: '2px' }}>{n.message}</div>
              )}
            </div>
            <button
              onClick={() => dismiss(n.id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--os-dim)', padding: '0', display: 'flex', flexShrink: 0 }}
            >
              <X size={13} />
            </button>
          </div>
        )
      })}
      <style>{`@keyframes slideIn{from{transform:translateX(20px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
    </div>
  )
}
